import { Dispatch, SetStateAction, useEffect } from "react"
import { ButtonPrimary } from "@nova/component-btn"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    getGenericSaveableWorkspacePageContent,
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
} from "../../routing/WorkspaceRoutes"
import { NotificationSettingsForm } from "./NotificationSettingsForm"
import {
    useNotificationPreferences,
    useUpdateNotificationPreferences,
} from "./NotificationSettingsQueries"
import {
    loadDraft,
    resetDraft,
    selectDraft,
    selectIsDirty,
    selectIsSavingChanges,
    setIsSavingChanges,
    toNotificationSettingsDraft,
    updateDraft,
} from "./slice"

function NotificationSettingsPageContent() {
    const { preferences, isLoading } = useNotificationPreferences()
    const dispatch = useAppDispatch()
    const draft = useAppSelector(selectDraft)

    useEffect(() => {
        if (preferences && !draft) dispatch(loadDraft(toNotificationSettingsDraft(preferences)))
    }, [preferences, draft, dispatch])

    useEffect(() => {
        return () => {
            dispatch(resetDraft())
        }
    }, [dispatch])

    if (isLoading || !draft) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    return (
        <div className="max-w-[56rem]">
            <NotificationSettingsForm
                value={draft}
                onChange={(next) => dispatch(updateDraft(next))}
                currentSilenceUntil={preferences?.silenceUntil}
            />
        </div>
    )
}

function NotificationSettingsPageFooter({
    setHasUnsavedChanges,
}: {
    setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>
}) {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(selectDraft)
    const isDirty = useAppSelector(selectIsDirty)
    const isSavingChanges = useAppSelector(selectIsSavingChanges)
    const { updateNotificationPreferences } = useUpdateNotificationPreferences()

    useEffect(() => {
        setHasUnsavedChanges?.(isDirty)
    }, [isDirty, setHasUnsavedChanges])

    const handleSave = async () => {
        if (!draft) return
        dispatch(setIsSavingChanges(true))
        try {
            const saved = await updateNotificationPreferences(draft)
            if (!saved) throw new Error("No notification preferences returned")
            dispatch(loadDraft(toNotificationSettingsDraft(saved)))
            dispatch(
                setGlobalNotificationContext({
                    message: "Notification settings saved.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to save notification settings. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            dispatch(setIsSavingChanges(false))
        }
    }

    return (
        <div className="w-full h-full flex items-center gap-2">
            <ButtonPrimary
                title="Save Changes"
                disabled={!isDirty || isSavingChanges}
                isLoading={isSavingChanges}
                onClick={handleSave}
            />
        </div>
    )
}

export const NotificationSettingsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({ title: "Notifications" }),
    Content: getGenericSaveableWorkspacePageContent({
        Content: NotificationSettingsPageContent,
        Footer: NotificationSettingsPageFooter,
    }),
})
